import { Check, Eye, EyeOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { SearchGroup } from '../../../../components/index-page/SearchGroup';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import type { MenuItem } from '../../../../components/Menu';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { personDisplayName, usePersonNames } from '../../../human-resources/personnel/usePersonNames';
import { surveyRespondentsApi } from '../surveyApi';
import type { SurveyRespondent, SurveyResponseView } from '../surveyTypes';
import { answerOf, isMachineValue } from '../utils/responseAnswer';
import { SurveyBulkAssessModal } from './SurveyBulkAssessModal';
import { SurveyResponseViewModal } from './SurveyResponseViewModal';

/**
 * Responses › Assessment — TWO PANES (product call, 12 Oct 2026; it replaced a Respondent dropdown
 * over a half-width column):
 *
 * - LEFT, the employees who answered: a search box, a select-all, and a checkbox beside each name.
 *   Clicking a NAME opens that employee's answers on the right; the checkbox TICKS the row and
 *   opens nothing — two different acts, so two different targets.
 * - RIGHT, the open employee's answers, in the Configuration › Questionnaire view's design: one
 *   padded `<ProfileCard>`, the brand-topped header card and a numbered card per question.
 *
 * The search narrows the list IN THE BROWSER — by name, employee number and division — because
 * names are resolved here and never stored, so the server cannot search them. Select-all ticks
 * what the search is showing, and its own state is read against that same set.
 *
 * The respondents are the survey's live responses, newest first — one page of a hundred. **Names are resolved, never stored**: the rows carry `personId`, turned
 * into names in ONE batch; without `personnel.read` a name reads Hidden, and above the reader's
 * access level it reads Restricted and the response itself is refused (403), said as such.
 *
 * Assess and Delete sit behind the card's cog, on their own keys. The cost of the layout, accepted:
 * a deleted response is no longer listed, so it has no Restore on screen — the endpoint stays.
 */
export function SurveyIndividualView({
  surveyId,
  surveyActive,
  onChanged,
}: {
  surveyId: string;
  surveyActive: boolean;
  /** After an assessment or a delete — the page re-reads the counts that gate its tabs. */
  onChanged?: () => void;
}) {
  const canReadPeople = usePermission('personnel.read');
  const toast = useToast();

  const [respondents, setRespondents] = useState<SurveyRespondent[] | null>(null);
  const [picked, setPicked] = useState('');
  const [search, setSearch] = useState('');
  // Ticked rows, by response id. A set, so a tick is one lookup per row.
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const [version, setVersion] = useState(0);

  const [view, setView] = useState<SurveyResponseView | null>(null);
  const [viewError, setViewError] = useState<ApiError | null>(null);

  /*
    Whether the open employee's answers are drawn. By DEFAULT an assessed response hides them and
    an unassessed one shows them; the cog's Show / Hide answers overrides that for the employee
    it was clicked on, and for this visit only — it is a view of the screen, not data.
  */
  const [answersChoice, setAnswersChoice] = useState<{ id: string; shown: boolean } | null>(null);
  const [assessing, setAssessing] = useState(false);
  const [bulkAssessing, setBulkAssessing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    surveyRespondentsApi
      .list(surveyId, { pageSize: 100, status: 'active', sort: 'respondedAt:desc' }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setRespondents(page.items);
      })
      .catch(() => {
        if (!controller.signal.aborted) setRespondents([]);
      });

    return () => controller.abort();
  }, [surveyId, version]);

  // `!= null`: a withheld id arrives as undefined, not null.
  const personIds = useMemo(
    () => (canReadPeople ? (respondents ?? []).map((r) => r.personId).filter((id): id is string => id != null) : []),
    [canReadPeople, respondents],
  );

  const names = usePersonNames(personIds);

  const nameOf = useCallback(
    (row: SurveyRespondent) => (!canReadPeople && row.personId != null ? 'Hidden' : personDisplayName(row, names)),
    [canReadPeople, names],
  );

  // The newest response until the reader picks another — derived, so a deleted pick falls back.
  const selected = respondents?.find((r) => r.id === picked) ?? respondents?.[0];
  const selectedId = selected?.id ?? '';

  useEffect(() => {
    if (!selectedId) return;

    const controller = new AbortController();

    surveyRespondentsApi
      .get(surveyId, selectedId, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;

        setView(next);
        setViewError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setView(null);
        setViewError(caught instanceof ApiError ? caught : new ApiError('Could not load the response.', 0));
      });

    return () => controller.abort();
  }, [surveyId, selectedId, version]);

  const deletedSurvey = surveyActive ? null : 'This survey is deleted. Restore it before changing its respondents.';

  async function runDelete() {
    if (!selected) return;

    setRunning(true);
    setError(null);

    try {
      await surveyRespondentsApi.deactivate(surveyId, selected.id);

      toast.success('Deleted the response.');
      setDeleting(false);
      setPicked('');
      setVersion((v) => v + 1);
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  // What the search is showing. Name, employee number and division — all on the row or resolved here.
  const term = search.trim().toLowerCase();
  const listed = useMemo(
    () =>
      (respondents ?? []).filter(
        (r) =>
          term === '' ||
          [nameOf(r), r.employeeNumber, r.orgUnitName ?? ''].some((value) => value.toLowerCase().includes(term)),
      ),
    [respondents, term, nameOf],
  );

  // Ticks that still name a live response — a deleted one drops out without an effect to clear it.
  const live = useMemo(() => new Set((respondents ?? []).map((r) => r.id)), [respondents]);
  const tickedCount = [...ticked].filter((id) => live.has(id)).length;
  const listedTicked = listed.filter((r) => ticked.has(r.id)).length;
  const allTicked = listed.length > 0 && listedTicked === listed.length;

  function toggle(id: string) {
    setTicked((current) => {
      const next = new Set(current);

      if (!next.delete(id)) next.add(id);

      return next;
    });
  }

  /** Ticks everything the search is showing — or unticks it, when it is all ticked already. */
  function toggleAll() {
    setTicked((current) => {
      const next = new Set(current);

      for (const r of listed) {
        if (allTicked) next.delete(r.id);
        else next.add(r.id);
      }

      return next;
    });
  }

  // Of the ticked, those still to assess — and within the reader's access level.
  const tickedRows = (respondents ?? []).filter((r) => ticked.has(r.id));
  const assessable = tickedRows.filter((r) => r.assessedAt == null && !r.isRestricted);
  const bulkReason =
    deletedSurvey ?? (assessable.length === 0 ? 'Every selected response is already assessed, or above your access level.' : null);

  if (respondents === null) return <Skeleton className="m-4 h-40" />;

  if (respondents.length === 0) return <EmptyState message="No one has responded to this survey yet." />;

  const name = selected ? nameOf(selected) : '';
  // The view is the selected one's only once it has arrived — a stale one never shows under a new name.
  const shown = view?.id === selectedId ? view : null;

  const answersShown =
    answersChoice != null && answersChoice.id === selectedId ? answersChoice.shown : selected?.assessedAt == null;

  const menuItems: MenuItem[] = selected
    ? [
        {
          key: 'toggle-answers',
          label: answersShown ? 'Hide answers' : 'Show answers',
          icon: answersShown ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />,
          onClick: () => setAnswersChoice({ id: selectedId, shown: !answersShown }),
        },
        {
          key: 'assess',
          label: 'Assess',
          permission: 'surveys.assess',
          disabledReason:
            deletedSurvey ??
            (selected.assessedAt != null
              ? 'This response has already been assessed.'
              : selected.isRestricted
                ? "This employee's record is above your access level."
                : null),
          onClick: () => setAssessing(true),
        },
        {
          key: 'delete',
          label: 'Delete',
          destructive: true,
          permission: 'surveys.update',
          disabledReason: deletedSurvey,
          onClick: () => setDeleting(true),
        },
      ]
    : [];

  return (
    <div className="flex h-full min-h-0 min-w-0">
      {/* LEFT — who answered. Its own scroll region; the header (search, select-all) stays put. */}
      <aside
        aria-label="Employees who answered"
        className="flex w-80 shrink-0 flex-col border-r border-slate-200"
      >
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4">
          <SearchGroup
            id="survey-individual"
            value={search}
            onChange={setSearch}
            placeholder="Search employees"
            total={listed.length}
          />

          <label
            htmlFor="survey-individual-select-all"
            className="flex items-center gap-3 text-sm text-slate-600"
          >
            <Checkbox
              id="survey-individual-select-all"
              checked={allTicked}
              indeterminate={listedTicked > 0 && !allTicked}
              disabled={listed.length === 0}
              onChange={toggleAll}
            />
            Select all
            {tickedCount > 0 ? (
              <span className="ml-auto font-mono text-xs tabular-nums text-slate-500">{tickedCount} selected</span>
            ) : null}
          </label>

          {/*
            SEVERAL TICKED → assess them together (product call, 13 Oct 2026). Drawn only from two
            up: one employee is assessed from their own card. Disabled, with the reason, when none
            of the ticked ones is still to assess.
          */}
          {tickedCount > 1 ? (
            <PermissionGate permission="surveys.assess">
              {bulkReason != null ? (
                // Wrapped only when there is something to say — a disabled button fires no hover of its own.
                <Tooltip label={bulkReason} disabledReason={bulkReason} block>
                  <Button id="survey-individual-assess-selected" variant="primary" className="w-full" disabled>
                    Assess selected
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  id="survey-individual-assess-selected"
                  variant="primary"
                  className="w-full"
                  onClick={() => setBulkAssessing(true)}
                >
                  Assess selected
                </Button>
              )}
            </PermissionGate>
          ) : null}
        </div>

        <ul className="modern-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {listed.length === 0 ? (
            <li className="p-4 text-sm text-slate-500">No employee matches that search.</li>
          ) : null}

          {listed.map((r) => {
            const rowName = nameOf(r);
            const open = r.id === selectedId;

            return (
              <li
                key={r.id}
                className={`flex items-start gap-3 border-b border-slate-100 px-4 py-2.5 ${open ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
              >
                <Checkbox
                  id={`survey-individual-tick-${r.id}`}
                  className="mt-0.5"
                  checked={ticked.has(r.id)}
                  onChange={() => toggle(r.id)}
                  aria-label={`Select ${rowName}`}
                />

                {/* The NAME opens the answers; the open row is the darkest text, on the tint. */}
                <button
                  type="button"
                  id={`survey-individual-open-${r.id}`}
                  aria-current={open ? 'true' : undefined}
                  onClick={() => setPicked(r.id)}
                  className="flex min-w-0 flex-1 flex-col text-left"
                >
                  <span className={`truncate text-sm ${open ? 'text-slate-900' : 'text-slate-600'}`}>{rowName}</span>
                  <span className="truncate text-xs text-slate-500">
                    {[r.employeeNumber, r.orgUnitName].filter(Boolean).join(' · ')}
                  </span>
                </button>

                {/*
                  Assessed — a check (product call, 12 Oct 2026; it was a pill). The icon alone
                  would be colour and shape only, so the word rides in the tooltip and an
                  `sr-only` twin.
                */}
                {r.assessedAt != null ? (
                  <Tooltip label="Assessed">
                    <span className="mt-0.5 inline-flex shrink-0 text-emerald-600">
                      <Check className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Assessed</span>
                    </span>
                  </Tooltip>
                ) : null}
              </li>
            );
          })}
        </ul>
      </aside>

      {/* RIGHT — the open employee's answers. */}
      <div className="modern-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <ProfileCard id="survey-profile-individual" title="Assessment" menuItems={menuItems} padded>
          {viewError ? (
            <ErrorState message={viewError.message} correlationId={viewError.correlationId} />
          ) : !shown ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="flex flex-col gap-3">
              {/* The assessment, once made: who, when, and what they wrote — FIRST, above the answers (product call, 13 Oct 2026). */}
              {shown.assessedAt ? (
                <div role="status" className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-xs text-slate-600">
                    Assessed by {shown.assessedByName ?? 'an unknown account'} on{' '}
                    <time dateTime={toIso(shown.assessedAt)} className="font-mono">
                      {formatDateTime(shown.assessedAt)}
                    </time>
                    . This employee is on the Development Plan.
                  </p>
                  {shown.assessmentRemarks ? (
                    <p className="whitespace-pre-line text-sm text-slate-600">{shown.assessmentRemarks}</p>
                  ) : null}
                </div>
              ) : null}
              {/*
                THE ANSWERS — hidden once the response is assessed, leaving the assessment alone
                (product call, 13 Oct 2026), and brought back from the card's cog.
              */}
              {answersShown ? (
                <>
                <div className="rounded-xl border border-slate-200 border-t-4 border-t-brand-500 bg-white p-5">
                  <h2 className="text-lg text-slate-800">{shown.questionnaireTitle}</h2>
                  {shown.questionnaireDescription ? (
                    <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{shown.questionnaireDescription}</p>
                  ) : null}
                  <p className="mt-2 text-sm text-slate-500">
                    {name}
                    {selected?.orgUnitName ? ` · ${selected.orgUnitName}` : null} · Responded{' '}
                    <time dateTime={toIso(shown.respondedAt)} className="font-mono">
                      {formatDateTime(shown.respondedAt)}
                    </time>
                  </p>
                </div>

                {shown.questions.length === 0 ? (
                  <p className="px-1 text-xs text-slate-500">This questionnaire has no questions.</p>
                ) : null}

                {shown.questions.map((question, index) => {
                  const answer = answerOf(question, name);

                  return (
                    <div
                      key={question.questionId}
                      className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5"
                    >
                      <div className="flex gap-2">
                        <span className="shrink-0 font-mono text-sm tabular-nums text-slate-500">{index + 1}.</span>
                        <div className="flex min-w-0 flex-col gap-1">
                          <p className="text-sm text-slate-800">
                            {question.text}
                            {question.isRequired ? <span className="ml-1 text-red-600">*</span> : null}
                          </p>
                          {question.description ? (
                            <p className="whitespace-pre-line text-xs text-slate-500">{question.description}</p>
                          ) : null}
                        </div>
                      </div>

                      {/* An unanswered question says so — a blank would read as a rendering fault. */}
                      {answer ? (
                        <p
                          className={`whitespace-pre-line text-sm text-slate-600 ${isMachineValue(question.type) ? 'font-mono' : ''}`}
                        >
                          {answer}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400">Not answered</p>
                      )}
                    </div>
                  );
                })}

                <div className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-800">Remarks</p>
                  {shown.remarks ? (
                    <p className="whitespace-pre-line text-sm text-slate-600">{shown.remarks}</p>
                  ) : (
                    <p className="text-sm text-slate-400">—</p>
                  )}
                </div>
                </>
              ) : (
                <p className="px-1 text-xs text-slate-500">The answers are hidden. Show them from this card&apos;s settings.</p>
              )}

            </div>
          )}
        </ProfileCard>
      </div>
      </div>

      {bulkAssessing ? (
        <SurveyBulkAssessModal
          open
          surveyId={surveyId}
          targets={assessable.map((r) => ({ id: r.id, name: nameOf(r), employeeNumber: r.employeeNumber }))}
          skipped={tickedRows.length - assessable.length}
          onClose={() => {
            setBulkAssessing(false);
            // One assessed before a later one was refused is already assessed.
            setVersion((v) => v + 1);
            onChanged?.();
          }}
          onAssessed={() => {
            setBulkAssessing(false);
            setTicked(new Set());
            setVersion((v) => v + 1);
            onChanged?.();
          }}
        />
      ) : null}

      {/* Assess is the answers window's own Assessment section — one way to assess, not two. */}
      {assessing && selected ? (
        <SurveyResponseViewModal
          open
          surveyId={surveyId}
          respondentId={selected.id}
          respondentName={name}
          assessOnOpen
          onClose={() => setAssessing(false)}
          onAssessed={() => {
            setVersion((v) => v + 1);
            onChanged?.();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(false);
            setError(null);
          }}
          onConfirm={() => void runDelete()}
          title="Delete response"
          danger
          requireConfirm
          requireCredential
          loading={running}
          id="survey-respondent-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                This response leaves the list and the respondent count, and the employee may answer the questionnaire
                again. Nothing is erased — its answers are kept.
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
